import { LoadingOutlined } from '@ant-design/icons'
import { DatePicker, Label, Select } from '@loadsmart/loadsmart-ui'
import {
  Drawer,
  Layout,
  Text,
  ToggleGroup,
  TextField,
  Accordion,
  Switch,
  TextArea,
  Field,
  Icon,
} from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-tokens'
import { Spin } from 'antd/lib'
import { Form, Formik, FormikConfig, FormikProps } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { shallowEqual, useSelector } from 'react-redux'
import styled from 'styled-components'
import * as Yup from 'yup'

import { metadataClient } from '../../../clients/MetadataClient'
import { driverClient } from '../../../clients/DriverClients'
import type { SiteDirectory } from '../../../models/client/site'
import type { User } from '../../../models/client/user'
import { selectUserToken } from '../../../store/user'
import {
  REQUIRED_FIELD_MESSAGE,
  TRUCK_CLASS_OPTIONS,
  TRUCK_FUEL_TYPE_OPTIONS,
} from '../../constants/reports'
import { useAsset } from '../../hooks/useAsset'
import type { AddAssetForm, AssetData, Gate, Location } from '../../../models/client/reports'
import { getAllowedSites, getFormInitialValues, getSelectOptions } from '../../utils/reports'
import { useMessage } from '../../../hooks/useMessage'
import { useLocationGates } from '../../hooks/useLocationGates'
import EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import { carrierClient } from '../../../clients/CarrierClient'
import { utcToDisplayDate, utcToMilitaryDisplayTime } from '../../../util/datetime'
import analytics, { AnalyticsEvent, AnalyticsEventTrigger } from '../../../util/analytics'

type AddAssetDrawerProps = {
  open: boolean
  handleAddAssetClose: () => void
  handleEditAssetClose: () => void
  user: User
  locations: SiteDirectory
  selectedLocation: Location
  asset: any
  refresh: () => void
  isGateFeedAsset?: boolean
}

type AddAssetDrawerFormProps = {
  open: boolean
  handleAddAssetClose: () => void
  handleEditAssetClose: () => void
  gates: Gate[]
  token: string | null
  assetData?: AssetData
  loadTypes: any
  vehicleTypes: any
  fuelTypes: any
  allowedSites: any
  addAsset: (draft: any, meta?: any, options?: any) => Promise<void>
  editAsset: (observationId: string, draft: any, meta: any, options?: any) => Promise<void>
  isAdding: boolean
  isEditing: boolean
  isGateFeedAsset?: boolean
}

const validationSchema = Yup.object().shape({
  location: Yup.object({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }).required(REQUIRED_FIELD_MESSAGE),
  gate: Yup.object({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }).required(REQUIRED_FIELD_MESSAGE),
  assetStatus: Yup.string().required(REQUIRED_FIELD_MESSAGE),
  ingateDate: Yup.string().required(REQUIRED_FIELD_MESSAGE),
  ingateTime: Yup.string().required(REQUIRED_FIELD_MESSAGE),
  outgateDate: Yup.string().when('assetStatus', ([value]) => {
    if (value === 'outgated') {
      return Yup.string().required(REQUIRED_FIELD_MESSAGE)
    }
    return Yup.string()
  }),
  outgateTime: Yup.string().when('assetStatus', ([value]) => {
    if (value === 'outgated') {
      return Yup.string().required(REQUIRED_FIELD_MESSAGE)
    }
    return Yup.string()
  }),
  loadType: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string(),
    })
    .test('is-required-with-load-details', REQUIRED_FIELD_MESSAGE, function (value) {
      const { loadNumber, sealPresent, sealNumber, loadLPNumber, bco } = this.parent
      if (loadNumber || sealPresent || sealNumber || loadLPNumber || bco) {
        return !!value?.label && !!value?.value
      }
      return true
    })
    .nullable(),
  loadNumber: Yup.string().nullable(),
  sealPresent: Yup.boolean().nullable(),
  sealNumber: Yup.string().nullable(),
  loadLPNumber: Yup.string().nullable(),
  bco: Yup.string().nullable(),
  loadRemarks: Yup.string().nullable(),
  chassisNumber: Yup.string().nullable(),
  chassisLPNumber: Yup.string().nullable(),
  chassisRemarks: Yup.string().nullable(),
  vehicleType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).nullable(),
  carrier: Yup.object({
    label: Yup.string().nullable(),
    value: Yup.string().nullable(),
  }).nullable(),
  truckNumber: Yup.string().nullable(),
  truckDOT: Yup.string().nullable(),
  truckLPNumber: Yup.string().nullable(),
  truckClass: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).nullable(),
  fuelType: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  }).nullable(),
  vehicleRemarks: Yup.string().nullable(),
})

const StyledGrid = styled(Layout.Grid)`
  max-width: 288px;
`

const StyledDrawerBody = styled(Drawer.Body)`
  display: grid;
  text-align: center;
  align-items: center;
  height: 100%;
`

function AddAssetDrawerForm({
  open,
  handleAddAssetClose,
  handleEditAssetClose,
  assetData,
  loadTypes,
  vehicleTypes,
  fuelTypes,
  allowedSites,
  token,
  gates,
  addAsset,
  editAsset,
  isAdding,
  isEditing,
  isGateFeedAsset,
}: AddAssetDrawerFormProps) {
  const loadTypeOptions = getSelectOptions(loadTypes)
  const vehicleTypeOptions = getSelectOptions(vehicleTypes)
  const fuelTypeOptions = getSelectOptions(fuelTypes)

  const defaultVehicleType = vehicleTypeOptions[0]
  const defaultLoadType = loadTypeOptions[0]

  const initialValues = getFormInitialValues(
    assetData,
    allowedSites,
    gates,
    defaultVehicleType,
    defaultLoadType
  )

  const assetStatusOptions = isGateFeedAsset
    ? [
        { label: 'Ingate', value: 'in-the-yard' },
        { label: 'Outgate', value: 'outgated' },
      ]
    : [{ label: 'In the yard', value: 'in-the-yard' }]

  const drawerTitle = isGateFeedAsset ? 'transaction' : 'asset'

  const [activeLocation, setActiveLocation] = useState(allowedSites[0])
  const [activeGates, setActiveGates] = useState(gates)

  const onSubmit: FormikConfig<AddAssetForm>['onSubmit'] = useCallback(
    async (values) => {
      const analyticsValues = {
        locationId: values.location?.value,
        locationName: values.location?.label,
        loadType: values.loadType,
        loadDamage: values.loadRemarks,
        sealPresent: values.sealPresent,
        chassisDamage: values.chassisRemarks,
        vehicleType: values.vehicleType,
        vehicleDamage: values.vehicleRemarks,
        fuelType: values.fuelType,
        vehicleClass: values.truckClass,
      }

      const updateObsTrackingEvent = isGateFeedAsset
        ? AnalyticsEvent.ReportsUpdateTransaction
        : AnalyticsEvent.ReportsUpdateAsset

      if (assetData?.id) {
        analytics.track(updateObsTrackingEvent, AnalyticsEventTrigger.success, analyticsValues)
        return await editAsset(assetData.id, values, assetData, {
          report: !isGateFeedAsset,
        })
      }

      const newObsTrackingEvent = isGateFeedAsset
        ? AnalyticsEvent.ReportsNewTransaction
        : AnalyticsEvent.ReportsNewAsset

      analytics.track(newObsTrackingEvent, AnalyticsEventTrigger.success, analyticsValues)

      return await addAsset(values, assetData, { report: !isGateFeedAsset })
    },
    [assetData]
  )

  const searchCarriers = () => {
    return {
      type: 'carriers',
      adapter: {
        getKey: (carrier) => carrier.id,
        getLabel: (carrier) => carrier.name,
      },
      async fetch({ query }) {
        if (!token) {
          return []
        }

        if (query.length < 3) {
          return []
        }

        const carriers = await carrierClient.getCarriers({ token, search: query })

        const adaptedCarriers = Object.entries(carriers).map(([id, carrier]) => ({
          id,
          name: carrier.name,
        }))

        return adaptedCarriers || []
      },
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({
        handleChange,
        setFieldValue,
        resetForm,
        values,
        errors,
        isValid,
        dirty,
      }: FormikProps<AddAssetForm>) => {
        const gatesByLocation = useLocationGates(activeLocation)

        useEffect(() => {
          setActiveGates(gatesByLocation)
          setFieldValue('gate', gatesByLocation[0])
        }, [gatesByLocation, values.location])

        const onClose = () => {
          resetForm({})
          handleAddAssetClose()
        }

        return (
          <Drawer open={open} onClose={onClose} size="medium">
            <Drawer.Header>
              <Text variant="heading-md-bold">
                {assetData ? `Edit ${drawerTitle}` : `Add ${drawerTitle}`}
              </Text>
              <Drawer.Close />
            </Drawer.Header>

            <Drawer.Body>
              <Form id="asset-form">
                <Layout.Stack gap="spacing-10">
                  <Layout.Stack gap="spacing-5">
                    <Text variant="heading-sm-bold">Gate transactions</Text>

                    <Layout.Grid>
                      <Field status={errors.location ? 'danger' : 'default'} required>
                        <Field.Label>Location</Field.Label>
                        <Select
                          name="location"
                          options={allowedSites}
                          placeholder="Select location"
                          value={values.location}
                          onChange={(e) => {
                            handleChange('location')
                            setActiveLocation(
                              allowedSites.find((site) => site.value === e.target.value?.value)
                            )
                          }}
                          disabled={isGateFeedAsset && !!assetData}
                          hideClear
                        />
                        {errors.location ? <Field.Hint>Required</Field.Hint> : null}
                      </Field>
                      <Field status={errors.gate ? 'danger' : 'default'} required>
                        <Field.Label>Gate</Field.Label>
                        <Select
                          name="gate"
                          options={activeGates}
                          placeholder="Select gate"
                          value={values.gate}
                          onChange={handleChange}
                          disabled={isGateFeedAsset && !!assetData}
                          hideClear
                        />
                        {errors.gate ? <Field.Hint>Required</Field.Hint> : null}
                      </Field>
                    </Layout.Grid>

                    <Field>
                      <Field.Label>{isGateFeedAsset ? 'Direction' : 'Asset status'}</Field.Label>
                      <ToggleGroup
                        id="assetStatus"
                        type="single-strict"
                        options={assetStatusOptions}
                        value={values.assetStatus}
                        onChange={(e) => setFieldValue('assetStatus', e.target.value)}
                      />
                    </Field>

                    <Layout.Grid>
                      {assetData ? (
                        <Layout.Stack gap="spacing-1">
                          <Text color="color-neutral-80">Recorded:</Text>
                          <Layout.Group>
                            <Layout.Group gap="spacing-1" align="center">
                              <Icon name="calendar" color="color-neutral-80" />
                              {utcToDisplayDate(
                                assetData.detectedAt,
                                assetData.gate?.site?.timezone
                              )}
                            </Layout.Group>
                            <Layout.Group gap="spacing-1" align="center">
                              <Icon name="clock" color="color-neutral-80" />
                              {utcToMilitaryDisplayTime(
                                assetData.detectedAt,
                                assetData.gate?.site?.timezone
                              )}
                            </Layout.Group>
                          </Layout.Group>
                        </Layout.Stack>
                      ) : (
                        <StyledGrid>
                          <Field status={errors.ingateDate ? 'danger' : 'default'}>
                            <Field.Label>{isGateFeedAsset ? 'Date' : 'Ingate date'}</Field.Label>
                            <DatePicker
                              name="ingateDate"
                              value={values.ingateDate}
                              onChange={handleChange}
                            />
                            {errors.ingateDate ? <Field.Hint>Required</Field.Hint> : null}
                          </Field>
                          <Field status={errors.ingateTime ? 'danger' : 'default'}>
                            <Field.Label>{isGateFeedAsset ? 'Time' : 'Ingate time'}</Field.Label>
                            <TextField
                              name="ingateTime"
                              type="time"
                              value={values.ingateTime}
                              onChange={handleChange}
                            />
                            {errors.ingateTime ? <Field.Hint>Required</Field.Hint> : null}
                          </Field>
                        </StyledGrid>
                      )}
                    </Layout.Grid>
                  </Layout.Stack>

                  <Layout.Stack>
                    <Text variant="heading-sm-bold">Asset details</Text>
                    <Text variant="body-md">
                      Add details to at least one of the assets below to continue
                    </Text>

                    <Accordion expanded>
                      <Accordion.Title>Load details</Accordion.Title>
                      <Accordion.Content>
                        <Layout.Stack>
                          <Field status={errors.loadType ? 'danger' : 'default'}>
                            <Field.Label>Load type</Field.Label>
                            <Select
                              name="loadType"
                              options={loadTypeOptions}
                              placeholder="Select load type"
                              value={values.loadType}
                              onChange={handleChange}
                            />
                            {errors.loadType ? <Field.Hint>{errors.loadType}</Field.Hint> : null}
                          </Field>
                          <Field status={errors.loadNumber ? 'danger' : 'default'}>
                            <Field.Label>Load number</Field.Label>
                            <TextField
                              name="loadNumber"
                              type="text"
                              placeholder="Enter load number"
                              value={values.loadNumber}
                              onChange={handleChange}
                            />
                            {errors.loadNumber ? (
                              <Field.Hint>{errors.loadNumber}</Field.Hint>
                            ) : null}
                          </Field>
                          <Layout.Group align="center" gap="spacing-2">
                            <Switch
                              name="sealPresent"
                              checked={values.sealPresent}
                              onChange={(e) => setFieldValue('sealPresent', e.target.checked)}
                            />
                            <Text variant="body-md" color="color-text-tertiary">
                              Seal present
                            </Text>
                          </Layout.Group>
                          <Field>
                            <Field.Label>Seal number</Field.Label>
                            <TextField
                              name="sealNumber"
                              type="text"
                              placeholder="Enter seal number"
                              onChange={handleChange}
                              value={values.sealNumber}
                            />
                          </Field>
                          <Layout.Grid>
                            <Field>
                              <Field.Label>Load LP number</Field.Label>
                              <TextField
                                name="loadLPNumber"
                                type="text"
                                placeholder="Enter load LP number"
                                value={values.loadLPNumber}
                                onChange={handleChange}
                              />
                            </Field>
                            <Field>
                              <Field.Label>BCO</Field.Label>
                              <TextField
                                name="bco"
                                type="text"
                                placeholder="Enter BCO number"
                                onChange={handleChange}
                                value={values.bco}
                              />
                            </Field>
                          </Layout.Grid>
                          <Field>
                            <Field.Label>Load remarks</Field.Label>
                            <TextArea
                              name="loadRemarks"
                              placeholder="Add any comments to this item"
                              rows={3}
                              cols={50}
                              onChange={handleChange}
                              value={values.loadRemarks}
                            />
                          </Field>
                        </Layout.Stack>
                      </Accordion.Content>
                    </Accordion>

                    <Accordion expanded>
                      <Accordion.Title>Chassis details</Accordion.Title>
                      <Accordion.Content>
                        <Layout.Stack>
                          <Field>
                            <Field.Label>Chassis number</Field.Label>
                            <TextField
                              name="chassisNumber"
                              type="text"
                              placeholder="Enter chassis number"
                              onChange={handleChange}
                              value={values.chassisNumber}
                            />
                          </Field>
                          <Field>
                            <Field.Label>Chassis LP number</Field.Label>
                            <TextField
                              name="chassisLPNumber"
                              type="text"
                              placeholder="Enter chassis LP number"
                              onChange={handleChange}
                              value={values.chassisLPNumber}
                            />
                          </Field>
                          <Field>
                            <Field.Label>Chassis remarks</Field.Label>
                            <TextArea
                              name="chassisRemarks"
                              placeholder="Add any comments to this item"
                              rows={3}
                              cols={50}
                              onChange={handleChange}
                              value={values.chassisRemarks}
                            />
                          </Field>
                        </Layout.Stack>
                      </Accordion.Content>
                    </Accordion>

                    <Accordion expanded>
                      <Accordion.Title>Vehicle details</Accordion.Title>
                      <Accordion.Content>
                        <Layout.Stack>
                          <Field>
                            <Field.Label>Vehicle type</Field.Label>
                            <Select
                              name="vehicleType"
                              options={vehicleTypeOptions}
                              placeholder="Select vehicle type"
                              onChange={handleChange}
                              value={values.vehicleType}
                            />
                          </Field>
                          <Field>
                            <Field.Label>Carrier</Field.Label>
                            <Select
                              name="carrier"
                              datasources={[searchCarriers]}
                              placeholder="Select carrier"
                              onChange={(event: EventLike<Selectable | Selectable[] | null>) => {
                                const carrier = event.target.value
                                setFieldValue('carrier', {
                                  label: carrier?.name,
                                  value: carrier?.id,
                                })
                              }}
                              value={values.carrier}
                            />
                          </Field>
                          <Field>
                            <Field.Label>Truck number</Field.Label>
                            <TextField
                              name="truckNumber"
                              type="text"
                              placeholder="Enter truck number"
                              onChange={handleChange}
                              value={values.truckNumber}
                            />
                          </Field>
                          <Field>
                            <Field.Label>Truck DOT</Field.Label>
                            <TextField
                              name="truckDOT"
                              type="text"
                              placeholder="Enter truck DOT"
                              onChange={handleChange}
                              value={values.truckDOT}
                            />
                          </Field>
                          <Field>
                            <Field.Label>Truck LP number</Field.Label>
                            <TextField
                              name="truckLPNumber"
                              type="text"
                              placeholder="Enter truck LP number"
                              onChange={handleChange}
                              value={values.truckLPNumber}
                            />
                            {errors.truckLPNumber ? (
                              <Field.Hint>{errors.truckLPNumber}</Field.Hint>
                            ) : null}
                          </Field>
                          <Layout.Grid>
                            <Field>
                              <Field.Label>Truck class</Field.Label>
                              <Select
                                name="truckClass"
                                options={TRUCK_CLASS_OPTIONS}
                                placeholder="Select truck class"
                                onChange={handleChange}
                                value={values.truckClass}
                              />
                            </Field>
                            <Field>
                              <Field.Label>Fuel type</Field.Label>
                              <Select
                                name="fuelType"
                                options={fuelTypeOptions}
                                placeholder="Select fuel type"
                                onChange={handleChange}
                                value={values.fuelType}
                              />
                            </Field>
                          </Layout.Grid>
                          <Field>
                            <Field.Label>Vehicle remarks</Field.Label>
                            <TextArea
                              name="vehicleRemarks"
                              placeholder="Add any comments to this item"
                              rows={3}
                              cols={50}
                              onChange={handleChange}
                              value={values.vehicleRemarks}
                            />
                          </Field>
                        </Layout.Stack>
                      </Accordion.Content>
                    </Accordion>
                  </Layout.Stack>
                </Layout.Stack>
              </Form>
            </Drawer.Body>
            <Drawer.Actions>
              <Drawer.ActionSecondary onClick={onClose}>Cancel</Drawer.ActionSecondary>
              <Drawer.ActionPrimary
                type="submit"
                form="asset-form"
                disabled={!isValid || !dirty || isAdding || isEditing}
              >
                Save asset
              </Drawer.ActionPrimary>
            </Drawer.Actions>
          </Drawer>
        )
      }}
    </Formik>
  )
}

export function AddAssetDrawer({
  open,
  handleAddAssetClose,
  handleEditAssetClose,
  user,
  locations,
  selectedLocation,
  asset,
  refresh,
  isGateFeedAsset,
}: AddAssetDrawerProps) {
  const token = useSelector(selectUserToken, shallowEqual)
  const allowedSites = getAllowedSites(user, locations)
  const gates = useLocationGates(selectedLocation)

  const messenger = useMessage()

  const {
    getObservationById: getAssetById,
    addObservation: addAsset,
    editObservation: editAsset,
    isAdding: isAddingAsset,
    isEditing: isEditingAsset,
    isSingleObservationLoading: isLoading,
  } = useAsset({
    onAddSuccess: () => {
      handleAddAssetClose()
      messenger.success('Asset added successfully')
      refresh()
    },
    onAddError: () => {
      messenger.error(new Error('Failed to add asset'))
    },
    onEditSuccess: () => {
      handleEditAssetClose()
      messenger.success('Asset updated successfully')
      refresh()
    },
    onEditError: () => {
      messenger.error(new Error('Failed to update asset'))
    },
  })

  const [assetData, setAssetData] = useState<AssetData>()

  const loadTypesClient = metadataClient.getLoadTypes.bind(null, {
    token: token || 'never',
  })
  const { data: loadTypes } = useQuery('load-type-picker', loadTypesClient, {
    enabled: !!token,
    refetchOnWindowFocus: false,
  })
  const vehicleTypesClient = metadataClient.getVehicleTypes.bind(null, {
    token: token || 'never',
  })
  const { data: vehicleTypes } = useQuery('vehicle-select', vehicleTypesClient, {
    enabled: !!token,
    refetchOnWindowFocus: false,
  })
  const fuelTypesClient = driverClient.getFuelTypesWithAttributes.bind(null)
  const { data: fuelTypes } = useQuery('fuel-types-select', fuelTypesClient, {
    enabled: !!token,
    refetchOnWindowFocus: false,
  })

  const fetchObservation = async () => {
    const response = await getAssetById(asset)
    setAssetData(response)
  }

  useEffect(() => {
    if (asset && open) {
      fetchObservation()
    } else {
      setAssetData(undefined)
    }
  }, [asset, open])

  if (isLoading || !gates) {
    return (
      <Drawer open={open} onClose={handleAddAssetClose} size="medium">
        <Drawer.Header>
          <Text variant="heading-md-bold">Edit asset</Text>
          <Drawer.Close />
        </Drawer.Header>

        <StyledDrawerBody>
          <Layout.Stack gap="spacing-5">
            <Spin indicator={<LoadingOutlined spin />} size="large" />
            <Text>Loading asset...</Text>
          </Layout.Stack>
        </StyledDrawerBody>
      </Drawer>
    )
  }

  return (
    <AddAssetDrawerForm
      open={open}
      handleAddAssetClose={handleAddAssetClose}
      handleEditAssetClose={handleEditAssetClose}
      token={token}
      gates={gates}
      assetData={assetData}
      loadTypes={loadTypes}
      vehicleTypes={vehicleTypes}
      fuelTypes={fuelTypes}
      allowedSites={allowedSites}
      addAsset={addAsset}
      editAsset={editAsset}
      isAdding={isAddingAsset}
      isEditing={isEditingAsset}
      isGateFeedAsset={isGateFeedAsset}
    />
  )
}
